import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchCareers } from "../redux/careersSlice";
import type { Career } from "../redux/careersSlice";

/* ─── TOKENS ─── */
const C = {
  black:   "#0A0A0A",
  dark:    "#111111",
  card:    "#181818",
  red:     "#E50914",
  redDark: "#B00710",
  white:   "#FFFFFF",
  offWhite:"#F0F0F0",
  gray:    "#999999",
  grayDim: "#404040",
  border:  "rgba(255,255,255,0.08)",
};

/* ─── Font ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

const VALUES = [
  { icon: "◆", title: "Remote First",   body: "Work from anywhere in the world. We trust outcomes over hours." },
  { icon: "◇", title: "EU Standards",   body: "Estonian incorporation means strong legal protection and EU work rights." },
  { icon: "▲", title: "Ship Fast",      body: "Small teams, big ownership. You'll see your work live in days, not quarters." },
  { icon: "○", title: "Grow Together",  body: "Equity, learning budgets, and a team that invests in your career." },
];

/* ════════════════════════════════════
   CAREERS
   ════════════════════════════════════ */
export default function Careers() {
  const navigate = useNavigate();
  const [activeDept, setActiveDept] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { careers, status } = useSelector((s: RootState) => s.careers);

  useEffect(() => {
    dispatch(fetchCareers());
  }, [dispatch]);

  /* Group only published careers by department */
  const departments = useMemo(() => {
    const published = careers.filter(c => c.published);
    const map = new Map<string, Career[]>();
    published.forEach(c => {
      if (!map.has(c.department)) map.set(c.department, []);
      map.get(c.department)!.push(c);
    });
    return Array.from(map, ([name, roles]) => ({ name, roles }));
  }, [careers]);

  return (
    <>
      <Helmet>
        <title>Careers —4REVA GROUP</title>
        <meta name="description" content="Join 4REVA GROUP — a technology holding building digital platforms across multiple industries worldwide." />
      </Helmet>

      <div style={{ backgroundColor: C.black, minHeight: "100vh", color: C.white, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── NAV ── */}
        <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: "rgba(10,10,10,0.95)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.border}`, padding: "0 6%", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <motion.div whileHover={{ scale: 1.02 }} onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 13, color: C.white }}>SG</div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, letterSpacing: "0.2em" }}>4REVA GROUP</span>
          </motion.div>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em" }}>← Home</button>
            <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate("/contact")}
              style={{ background: C.red, color: C.white, border: "none", padding: "10px 22px", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              CONTACT US
            </motion.button>
          </div>
        </nav>

        {/* ── HERO ── */}
        <div style={{ paddingTop: 68, background: `linear-gradient(135deg, ${C.dark} 0%, #0f0000 100%)`, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(229,9,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(229,9,20,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
          <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: `linear-gradient(to bottom, ${C.red}, transparent)` }} />

          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 6% 80px", position: "relative" }}>
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>
              Careers at 4REVA GROUP
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(40px, 6vw, 80px)", fontWeight: 400, lineHeight: 1.05, margin: "0 0 28px", maxWidth: 680 }}>
              Work that <em style={{ color: C.red }}>matters.</em><br />Teams that move.
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
              style={{ fontSize: 16, lineHeight: 1.8, color: "rgba(255,255,255,0.55)", maxWidth: 520, margin: "0 0 44px" }}>
              We're building technology that moves money, markets, and people across borders. Join a small team with outsized impact.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <motion.button whileHover={{ scale: 1.02, background: C.redDark }} whileTap={{ scale: 0.97 }}
                onClick={() => document.querySelector("#roles")?.scrollIntoView({ behavior: "smooth" })}
                style={{ background: C.red, color: C.white, border: "none", padding: "15px 32px", fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "background 0.2s" }}>
                SEE OPEN ROLES
              </motion.button>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={() => navigate("/contact")}
                style={{ background: "transparent", color: C.white, border: `1px solid rgba(255,255,255,0.25)`, padding: "15px 32px", fontWeight: 600, fontSize: 13, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                CONTACT US
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* ── VALUES ── */}
        <section style={{ padding: "80px 6%", background: C.dark, borderTop: `1px solid ${C.border}` }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", marginBottom: 48, textAlign: "center" }}>
              Why 4REVA GROUP
            </motion.p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2 }}>
              {VALUES.map((v, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  style={{ background: C.card, padding: "36px 28px", borderTop: `3px solid ${C.red}` }}>
                  <div style={{ fontSize: 24, marginBottom: 16, color: C.red }}>{v.icon}</div>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: C.white, marginBottom: 12 }}>{v.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,0.5)" }}>{v.body}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── OPEN ROLES ── */}
        <section id="roles" style={{ padding: "100px 6%", background: C.black }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 60 }}>
              <p style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 16px" }}>Open Positions</p>
              <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 400, margin: 0, lineHeight: 1.1 }}>Find your role</h2>
            </motion.div>

            {/* Loading */}
            {status === "loading" && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: C.gray, fontSize: 14 }}>
                Loading open positions…
              </div>
            )}

            {/* Empty state — no mock data */}
            {status !== "loading" && departments.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: "80px 40px",
                  textAlign: "center",
                  border: `1px solid ${C.border}`,
                  background: C.dark,
                }}>
                <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: C.white, margin: "0 0 12px" }}>
                  Check back soon
                </p>
                <p style={{ fontSize: 13, color: C.gray, margin: 0 }}>
                  No open positions right now. New roles are added regularly.
                </p>
              </motion.div>
            )}

            {/* Live departments */}
            {departments.map((dept, di) => (
              <motion.div
                key={dept.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: di * 0.1 }}
                style={{ marginBottom: 4 }}>

                {/* Department header */}
                <button
                  onClick={() => setActiveDept(activeDept === dept.name ? null : dept.name)}
                  style={{
                    width: "100%", background: C.card, border: "none",
                    borderLeft: `3px solid ${activeDept === dept.name ? C.red : C.grayDim}`,
                    padding: "22px 28px", display: "flex", alignItems: "center",
                    justifyContent: "space-between", cursor: "pointer",
                    transition: "border-color 0.2s", fontFamily: "'DM Sans', sans-serif",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.white, letterSpacing: "0.04em" }}>
                      {dept.name}
                    </span>
                    <span style={{ fontSize: 11, color: C.gray, letterSpacing: "0.12em" }}>
                      {dept.roles.length} open
                    </span>
                  </div>
                  <motion.span animate={{ rotate: activeDept === dept.name ? 180 : 0 }} style={{ color: C.gray, fontSize: 18 }}>
                    ▾
                  </motion.span>
                </button>

                {/* Role rows */}
                <AnimatePresence>
                  {activeDept === dept.name && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ overflow: "hidden" }}>
                      {dept.roles.map(role => (
                        <RoleRow
                          key={role.id}
                          role={role}
                          onApply={() => navigate("/application", { state: { role } })}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {/* Don't see a fit */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              style={{ marginTop: 60, padding: "48px 40px", background: C.dark, borderLeft: `3px solid ${C.red}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
              <div>
                <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: C.white, margin: "0 0 8px" }}>Don't see a fit?</p>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: 0 }}>We always want to hear from exceptional people. Send us a note.</p>
              </div>
              <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate("/contact")}
                style={{ background: C.red, color: C.white, border: "none", padding: "14px 32px", fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                GET IN TOUCH
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer style={{ background: "#080808", borderTop: `1px solid ${C.border}`, padding: "40px 6%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", margin: 0 }}>
            © {new Date().getFullYear()} 4REVA GROUP · Reg. 17381555 · Estonia, EU
          </p>
          <div style={{ display: "flex", gap: 24 }}>
            {[{ l: "Privacy", p: "/privacy" }, { l: "Terms", p: "/terms" }, { l: "Home", p: "/" }].map(x => (
              <span key={x.l} onClick={() => navigate(x.p)}
                style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", cursor: "pointer", transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = C.white)}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}>
                {x.l}
              </span>
            ))}
          </div>
        </footer>
      </div>
    </>
  );
}

/* ─── Role Row ─── */
function RoleRow({ role, onApply }: { role: Career; onApply: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "#1e1e1e" : "#141414",
        padding: "20px 28px 20px 44px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, transition: "background 0.2s",
      }}>
      <div>
        <p style={{ fontWeight: 600, fontSize: 15, color: C.white, margin: "0 0 6px" }}>{role.title}</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Tag small>{role.company}</Tag>
          <Tag small>{role.location}</Tag>
          <Tag small>{role.type}</Tag>
          {role.salary && <Tag small>{role.salary}</Tag>}
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.03 }}
        onClick={onApply}
        style={{
          background: "transparent", color: C.red,
          border: `1px solid ${C.red}`, padding: "10px 22px",
          fontWeight: 700, fontSize: 11, letterSpacing: "0.12em",
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = C.red; e.currentTarget.style.color = C.white; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.red; }}>
        APPLY
      </motion.button>
    </div>
  );
}

/* ─── Tag ─── */
function Tag({ children, small }: { children: React.ReactNode; small?: boolean }) {
  return (
    <span style={{
      fontSize: small ? 10 : 11, fontWeight: 600, letterSpacing: "0.1em",
      color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.06)",
      padding: small ? "3px 10px" : "5px 14px", textTransform: "uppercase",
    }}>
      {children}
    </span>
  );
}